/**
 * CaptureFab — the always-reachable quick-capture affordance (T12.1.5.1.1).
 *
 * A fixed ＋ button (with a mic badge) reachable in ≤1 tap from List / Deals /
 * Trip in both modes. Tapping it opens a TALL bottom sheet (most of the
 * viewport): a title + text input fixed at the top, then a scrollable region of
 * the household's existing products ("your products") that sits ABOVE the soft
 * keyboard (never clipped behind it). Tapping a product row — or submitting any
 * typed name — lands a ShoppingListItem on the active list.
 *
 * Capture path (T27.2.7.8 — create-on-the-fly): a ShoppingListItem needs a
 * HouseholdProduct (invariant I6). If the typed name matches an existing
 * HouseholdProduct we add a ShoppingListItem for it (no duplicate create);
 * otherwise we CREATE a HouseholdProduct via `handleAddProduct` — whose
 * best-match SystemProduct link lands silently on the backend, no "catalog /
 * system product" wording surfaces — then add the ShoppingListItem referencing
 * the returned id. Any typed name always adds; there is no dead-end.
 *
 * BUG27 — the picker is SERVER-SIDE searched while typing: the initial list is
 * the capped first page (`useHouseholdProducts`), but a non-empty name reads
 * `useSearchHouseholdProducts(query)` (a backend `ilike` over the whole
 * catalog). The match decision (exact-match / create-vs-add) runs against those
 * search results, so a product sorting past the capped row 100 now surfaces and
 * is reused instead of duplicated.
 *
 * BUG27 layer 3 — confirm-on-create nudge: choosing "Add '{X}'" for a brand-new
 * name does NOT create instantly. The suggestions region is replaced in place by
 * a confirm panel that surfaces up to 3 close existing products as tap-to-reuse
 * rows, one explainer line about why reuse keeps suggestions sharp, a single-tap
 * "Add as new product" create button, and a Back control. The title + input stay
 * put (the field keeps the typed text). An EXACT case-insensitive match never
 * reaches the confirm — it just adds the existing product. Back (and the first
 * hardware/Escape/dismiss while confirming) returns to the list; a second closes
 * the sheet. The fuller explainer shows once per session (a module-level flag),
 * the condensed line after.
 *
 * Keyboard-aware (T27.2.7.8): the sheet opens with the input focused and the
 * keyboard up; scrolling the suggestions past a small threshold blurs the input
 * to dismiss the soft keyboard so the list gets the full sheet, and tapping the
 * input restores it. The keyboard behaviour is real only in the native WebView
 * — a manual on-device gate; jsdom asserts the blur/focus calls.
 *
 * Fixed + safe-area-aware (native target); ≥44px target. One responsive
 * mobile-first shell: the floating ＋ button renders at every width (it is part
 * of the single mobile chrome). The sheet/overlay also renders at all widths,
 * so the planning list's inline "+ Add item" can open it via `openCapture`.
 * Tokens-only (CP5).
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type PointerEvent as ReactPointerEvent,
  type UIEvent as ReactUIEvent,
} from 'react';
import { Capacitor } from '@capacitor/core';
import {
  useActiveShoppingList,
  useHouseholdProducts,
  useSearchHouseholdProducts,
  useSuggestions,
} from '@shopista/shared/api/hooks';
import { apiErrorMessage } from '@shopista/shared/api';
import type {
  HouseholdProductWithDetails,
  ProductSuggestion,
  ShoppingListItemWithDetails,
} from '@shopista/shared/types';
import { useOrchestrators } from '../stores/orchestrator-context';
import { CAPTURE_OPEN_EVENT, type CaptureOpenDetail } from './capture-events';
import { randomUuid } from '../lib/randomUuid';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';
import { useOverlayRegistration } from '../lib/useOverlayRegistration';
import { Icon } from '../components';

// Exit-animation duration (ms) — matches the panel `shopista-sheet-out` keyframe
// in responsive.css; the sheet stays mounted this long after close so the
// slide-down plays before unmount (D1). Plain number — no token for timing.
const EXIT_MS = 200;
// Swipe-down distance (px) past which a drag dismisses the sheet (D2); below it
// the sheet snaps back.
const SWIPE_DISMISS_PX = 96;
// Scroll distance (px) in the suggestions region past which we dismiss the soft
// keyboard (blur the input). Mirrors the prototype's `capScrolled()` (scrollTop
// > 6) so a deliberate scroll hands the list the full sheet (T27.2.7.8).
const SCROLL_DISMISS_PX = 6;

// BUG27 layer 3 — the create-confirm copy (verbatim from the design section).
const CONFIRM_HEADING = 'Already in your products?';
const CONFIRM_EXPLAINER_FULL =
  'Your products are your own list. Reusing one keeps your suggestions and ' +
  'deals on target — only add a new product if it’s really different.';
const CONFIRM_EXPLAINER_CONDENSED =
  'Reusing an existing product keeps your suggestions on target.';
const CONFIRM_ADD_NEW_LABEL = 'Add as new product';
const CONFIRM_BACK_LABEL = 'Back';
// Up to this many close matches surface as tap-to-reuse rows.
const CONFIRM_MATCH_LIMIT = 3;

// Session-scoped flag (no persistence, resets on reload): the fuller explainer
// shows on the FIRST create-confirm a user sees this session, the condensed line
// after. A plain module-level boolean per the design section — not a store, not
// a counter, not a dismissible state.
let confirmExplainerSeen = false;
/** Test seam: reset the session explainer flag between tests. */
export function __resetConfirmExplainerForTest(): void {
  confirmExplainerSeen = false;
}

const fabStyle: CSSProperties = {
  position: 'fixed',
  right: 'var(--space-4)',
  // Clear the bottom nav (≈56px) + its safe-area inset.
  bottom: 'calc(4.5rem + env(safe-area-inset-bottom, 0px))',
  width: '3.5rem',
  height: '3.5rem',
  borderRadius: 'var(--radius-pill)',
  border: 'none',
  backgroundColor: 'var(--color-accent-primary)',
  color: 'var(--color-text-inverse)',
  fontSize: 'var(--font-size-xl)',
  boxShadow: 'var(--shadow-lg)',
  cursor: 'pointer',
  display: 'grid',
  placeItems: 'center',
  zIndex: 'var(--z-sticky)' as unknown as number,
};

const micBadgeStyle: CSSProperties = {
  position: 'absolute',
  right: '-0.125rem',
  bottom: '-0.125rem',
  width: '1.375rem',
  height: '1.375rem',
  borderRadius: 'var(--radius-pill)',
  backgroundColor: 'var(--color-bg-raised)',
  color: 'var(--color-accent-primary)',
  fontSize: 'var(--font-size-xs)',
  display: 'grid',
  placeItems: 'center',
  boxShadow: 'var(--shadow-sm)',
};

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'var(--color-overlay-scrim)',
  zIndex: 'var(--z-modal)' as unknown as number,
  display: 'flex',
  alignItems: 'flex-end',
};

// The tall sheet (T27.2.7.8) — most of the viewport, a flex COLUMN. Title +
// input are fixed at the top; the suggestions region is the only flex-grow
// child. `dvh`, not `vh` (H1): the soft-keyboard resize must shrink the sheet,
// not jump the layout. `overflow: hidden` keeps the panel itself from
// scrolling — only the inner suggestions region scrolls.
const panelStyle: CSSProperties = {
  width: '100%',
  height: '90dvh',
  maxHeight: '90dvh',
  backgroundColor: 'var(--color-bg-raised)',
  borderTopLeftRadius: 'var(--radius-lg)',
  borderTopRightRadius: 'var(--radius-lg)',
  padding: 'var(--space-4) var(--space-4)',
  paddingBottom: 'calc(var(--space-4) + env(safe-area-inset-bottom, 0px))',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
  overflow: 'hidden',
};

// Drag-handle grabber (D2) — the short pill at the sheet's top edge; its row is
// the swipe-capture surface.
const grabberRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  paddingBottom: 'var(--space-1)',
  marginTop: 'calc(-1 * var(--space-1))',
  touchAction: 'none',
  cursor: 'grab',
  flex: '0 0 auto',
};

const grabberStyle: CSSProperties = {
  width: '2.25rem',
  height: '0.25rem',
  borderRadius: 'var(--radius-pill)',
  backgroundColor: 'var(--color-border-strong)',
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-lg)',
  fontWeight: 'var(--font-weight-semibold)',
  flex: '0 0 auto',
};

// Title + input + section label all stay fixed at the top (flex: 0 0 auto); the
// suggestions region below them is the sole flex-grow child.
const formStyle: CSSProperties = {
  flex: '0 0 auto',
};

const fieldRowStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--space-2)',
  alignItems: 'center',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-strong)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-2) var(--space-3)',
};

const inputStyle: CSSProperties = {
  flex: 1,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontFamily: 'var(--font-family-sans)',
  fontSize: 'var(--font-size-base)',
  color: 'var(--color-text-primary)',
  caretColor: 'var(--color-accent-primary)',
};

const addButtonStyle: CSSProperties = {
  minWidth: '2.75rem',
  minHeight: '2.75rem',
  borderRadius: 'var(--radius-md)',
  border: 'none',
  backgroundColor: 'var(--color-accent-primary)',
  color: 'var(--color-text-inverse)',
  fontSize: 'var(--font-size-lg)',
  cursor: 'pointer',
};

const sectionLabelStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-medium)',
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  flex: '0 0 auto',
};

// The suggestions region — the ONLY flex-grow child of the column, so it takes
// all the space below the fixed title + input and sits ABOVE the keyboard. It
// scrolls internally (the household's products); the soft keyboard never clips
// it (T27.2.7.8 AC2). `maxHeight` stays `dvh`-capped (H1) for the keyboard
// resize; `flex: 1 1 auto` lets it shrink/grow with the sheet.
const listStyle: CSSProperties = {
  flex: '1 1 auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-1)',
  maxHeight: '70dvh',
  overflowY: 'auto',
  margin: 0,
  padding: 0,
  listStyle: 'none',
  WebkitOverflowScrolling: 'touch',
};

const productRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--space-2)',
  width: '100%',
  minHeight: '2.75rem',
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: 'none',
  backgroundColor: 'var(--color-bg-muted)',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-sans)',
  fontSize: 'var(--font-size-base)',
  fontWeight: 'var(--font-weight-medium)',
  textAlign: 'left',
  cursor: 'pointer',
};

const productRowAddStyle: CSSProperties = {
  color: 'var(--color-accent-primary)',
  fontSize: 'var(--font-size-lg)',
};

// T12.5 — the Suggested shortlist sits ABOVE "your products" as its own group:
// the same uppercase section label, then a short, non-growing list of rows. It
// is NOT the flex-grow scroll region (that stays the "your products" list); the
// shortlist is at most `limit` (10) rows and renders inline at the top.
const suggestionListStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-1)',
  margin: 0,
  padding: 0,
  listStyle: 'none',
  flex: '0 0 auto',
};

// A suggestion row stacks the product name over the endpoint's short reason
// line. Built on the shared product row (same height / hit target / token fill);
// the name + reason form a left-aligned column beside the ＋.
const suggestionTextColStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-1)',
  minWidth: 0,
};

const suggestionReasonStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-regular)',
  color: 'var(--color-text-muted)',
};

// "Add what I typed" affordance — the always-available create-on-the-fly row
// that surfaces when the typed name matches no existing product (T27.2.7.8 AC4).
const addTypedRowStyle: CSSProperties = {
  ...productRowStyle,
  borderWidth: '0.0625rem',
  borderStyle: 'dashed',
  borderColor: 'var(--color-accent-primary)',
  backgroundColor: 'transparent',
};

const hintStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  flex: '0 0 auto',
};

const errorStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-status-danger)',
  flex: '0 0 auto',
};

// The demoted voice slot — a footer hint, out of the input's primary action row
// (Flow A). The stub stays reachable but never occupies the type-to-add path.
const voiceFooterStyle: CSSProperties = {
  ...hintStyle,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-1)',
};

// BUG27 layer 3 — the create-confirm panel. It REPLACES the suggestions region
// in place (same flex-grow slot), so it scrolls internally above the keyboard
// like the list it stands in for.
const confirmPanelStyle: CSSProperties = {
  flex: '1 1 auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-2)',
  maxHeight: '70dvh',
  overflowY: 'auto',
  margin: 0,
  WebkitOverflowScrolling: 'touch',
};

// The prompt heading framing the close matches as the question (sectionLabel
// form bumped to --font-size-sm, sentence-case — not the uppercase list label).
const confirmHeadingStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-primary)',
  flex: '0 0 auto',
};

const confirmMatchListStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-1)',
  margin: 0,
  padding: 0,
  listStyle: 'none',
};

// The "how it works" nudge — one muted line (hint scale). No icon: the design's
// ⓘ is Designer-optional and the Icon set has no `info` glyph, so the line reads
// calm on its own (Coder's call to keep it light per the design section).
const confirmExplainerStyle: CSSProperties = {
  ...hintStyle,
};

// The single create commit — the established dashed-accent "creates something
// new" affordance (addTypedRowStyle) at full button height.
const confirmAddNewStyle: CSSProperties = {
  ...addTypedRowStyle,
  justifyContent: 'center',
  minHeight: '2.75rem',
};

// Back / cancel — a muted link-style text button, ≥44px tap height.
const confirmBackStyle: CSSProperties = {
  alignSelf: 'flex-start',
  minHeight: '2.75rem',
  padding: 'var(--space-2) var(--space-1)',
  border: 'none',
  background: 'transparent',
  color: 'var(--color-text-muted)',
  fontFamily: 'var(--font-family-sans)',
  fontSize: 'var(--font-size-sm)',
  cursor: 'pointer',
};

// Native-only keyboard-dismiss seam (T27.2.7.8). The input `.blur()` already
// closes the soft keyboard on both web and the WebView, so today this is a
// no-op behind the `isNativePlatform()` gate — it exists so a future native
// `Keyboard.hide()` (the @capacitor/keyboard plugin) has a home that never runs
// on web. Gating native behaviour behind `Capacitor.isNativePlatform()` keeps
// the web path on plain blur/focus (Deliverable 2).
function dismissNativeKeyboard(): void {
  if (!Capacitor.isNativePlatform()) return;
  // Reserved for an explicit native Keyboard.hide(); blur already suffices.
}

function findProductByName(
  products: HouseholdProductWithDetails[],
  name: string
): HouseholdProductWithDetails | undefined {
  const needle = name.trim().toLowerCase();
  return products.find((p) => p.display_name.trim().toLowerCase() === needle);
}

export function CaptureFab(): JSX.Element {
  const [open, setOpen] = useState(false);
  // Exit phase (D1): when the sheet closes we keep it mounted in `closing` for
  // EXIT_MS so the slide-down animation plays before unmount. The overlay renders
  // while `open || closing`.
  const [closing, setClosing] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // BUG27 layer 3 — the create-confirm step. When non-null, the suggestions
  // region is replaced by the confirm panel for this exact (trimmed) name; the
  // create only fires when the user taps "Add as new product". `confirmFuller`
  // is captured once when the panel opens (session-scoped) so the line doesn't
  // flip mid-view if another confirm renders.
  const [confirmName, setConfirmName] = useState<string | null>(null);
  const [confirmFuller, setConfirmFuller] = useState(false);
  // Focus targets so the panel lands focus inside the new step (a11y).
  const confirmHeadingRef = useRef<HTMLParagraphElement>(null);
  const confirmAddNewRef = useRef<HTMLButtonElement>(null);

  // Live swipe-down offset (px) while dragging the grabber (D2). Cleared on
  // release; drives an inline translateY so the panel tracks the finger.
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef<number | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const exitTimer = useRef<number | null>(null);

  const listQuery = useActiveShoppingList();
  const orchestrators = useOrchestrators();

  // BUG27 — drive the picker from a SERVER-SIDE search while the user is typing
  // (mirrors ProductsPage): the initial list is the capped first page
  // (`useHouseholdProducts`), but as soon as a non-empty name is typed we read
  // `useSearchHouseholdProducts(query)` — a backend `ilike` over the WHOLE
  // catalog. The capped 100-item page used to drop any match sorting past row
  // 100 (the "tomatoes" defect); the server search surfaces every match.
  const trimmed = text.trim();
  const query = trimmed.toLowerCase();
  const isSearching = trimmed.length > 0;

  const allQuery = useHouseholdProducts();
  const searchQuery = useSearchHouseholdProducts(trimmed);
  const activeQuery = isSearching ? searchQuery : allQuery;

  // The full initial catalog page — only used to seed the create-on-the-fly
  // optimistic row's household_id (any product carries it).
  const allProducts = allQuery.data?.products ?? [];

  // T12.5 — the "Suggested · likely needed soon" shortlist. The exclude source
  // is the active list's still-to-buy items (so something you still need to buy
  // never shows up as a suggestion). The server drops the excluded ids; the
  // shortlist renders ABOVE "your products" and only when not searching (empty
  // input) and the list is non-empty. A brand-new household with no history gets
  // an empty list → the group is simply not rendered (the sheet behaves exactly
  // as before).
  //
  // T12.5 reopen (2026-06-30) — exclude only to-buy rows (`pending`/`deferred`),
  // NOT `purchased`. A product earns its suggestion signal (purchase cadence,
  // pinned `cadenceDays`, always-keep-stocked) by being checked off, which marks
  // the row `purchased` — so excluding purchased rows hid every rebuy by
  // construction and made purchase-history-driven suggestions impossible.
  // Letting purchased fall through to scoring lets it resurface as a rebuy when
  // due (the recency penalty already suppresses a just-bought item). This mirrors
  // Planning's pending-only definition of "on the list". The shared `fetchActive`
  // still sends `include_checked=true` (shopping mode + Planning need purchased
  // rows); the filter is local to this exclude set.
  const onListProductIds = (listQuery.data?.items ?? [])
    .filter((item) => item.status !== 'purchased')
    .map((item) => item.householdproduct_id);
  const suggestionsQuery = useSuggestions(onListProductIds);
  const suggestions = suggestionsQuery.data?.suggestions ?? [];
  const showSuggestions = !isSearching && suggestions.length > 0;

  // T12.5 (prediction-led empty state) — the full alphabetical "your products"
  // browse list renders only when the user is searching (it carries the search
  // results + the create-on-the-fly row) OR the empty state has no suggestions
  // (the fallback so the sheet is never empty). With suggestions present and an
  // empty input, the Suggested group stands alone — the browse list is dropped.
  const showBrowse = isSearching || !showSuggestions;

  // The ACTIVE result set: server search results when searching, the initial
  // page otherwise. Every match decision (exact-match / create-vs-add) and the
  // rendered rows run against THIS set, never the capped initial page.
  const activeProducts = activeQuery.data?.products ?? [];

  // The backend `ilike` already filters to the query, so the active set IS the
  // list to render. (Defensive client substring keeps the rendered rows tight
  // if the server ever returns a superset.)
  const filteredProducts = isSearching
    ? activeProducts.filter((p) => p.display_name.toLowerCase().includes(query))
    : activeProducts;

  // Does the typed name exactly match an existing product (across the SEARCH
  // results, not the capped page)? When it does NOT and the field is non-empty,
  // the "add what I typed" create-on-the-fly row shows.
  const exactMatch = trimmed
    ? findProductByName(activeProducts, trimmed)
    : undefined;
  const showAddTyped = trimmed.length > 0 && !exactMatch;

  // BUG27 layer 3 — the up-to-3 close existing products surfaced inside the
  // confirm panel. Reuse the search's own ranking (no new ranking logic), drop
  // any exact case-insensitive match (it never reaches the panel — see AC), and
  // take the first 3. Resolved live against the current search results so a
  // surfaced match is a real, tappable HouseholdProduct.
  const confirmNeedle = (confirmName ?? '').trim().toLowerCase();
  const confirmMatches = confirmName
    ? activeProducts
        .filter((p) => p.display_name.trim().toLowerCase() !== confirmNeedle)
        .slice(0, CONFIRM_MATCH_LIMIT)
    : [];

  // Reset the transient field state — shared by the open path and the post-exit
  // cleanup so a reopened sheet always starts clean.
  const resetFields = useCallback(() => {
    setText('');
    setError(null);
    setBusy(false);
    setDragY(0);
    setConfirmName(null);
    dragStartY.current = null;
  }, []);

  // Close the sheet: flip to the exit phase, then unmount + reset after the
  // animation. A second call while already closing is a no-op (the guard
  // returns early when the sheet wasn't open).
  const closeSheet = useCallback(() => {
    setOpen((wasOpen) => {
      if (!wasOpen) return false;
      setClosing(true);
      if (exitTimer.current !== null) window.clearTimeout(exitTimer.current);
      exitTimer.current = window.setTimeout(() => {
        setClosing(false);
        resetFields();
        exitTimer.current = null;
      }, EXIT_MS);
      return false;
    });
  }, [resetFields]);

  // BUG27 layer 3 — leave the confirm step and return to the product list
  // without creating anything. The typed text is preserved so the user can keep
  // editing; focus returns to the input.
  const closeCreateConfirm = useCallback(() => {
    setConfirmName(null);
    setError(null);
    inputRef.current?.focus();
  }, []);

  // The single dismiss path shared by Escape, the overlay tap, the swipe-down,
  // and the Android hardware Back: while the confirm panel is showing, the FIRST
  // dismiss leaves the confirm step (returns to the list); only a second closes
  // the whole sheet. `confirmName` is read live via a ref-free closure — the
  // callback is rebuilt whenever it changes.
  const dismiss = useCallback(() => {
    if (confirmName !== null) {
      closeCreateConfirm();
      return;
    }
    closeSheet();
  }, [confirmName, closeCreateConfirm, closeSheet]);

  // The sheet is on screen while open OR mid-exit. Gates rendering + the body
  // scroll-lock (D3).
  const onScreen = open || closing;

  // Lock background scroll while the sheet is on screen (including the exit),
  // so the page can't scroll under a dismissing sheet (D3).
  useBodyScrollLock(onScreen);

  // T27.2.7.3 — register with the overlay stack while open so the Android
  // hardware Back button closes the capture sheet rather than popping the route
  // underneath it (MN2). Keyed on `open` (the intent flag), not `onScreen`, so
  // the entry leaves the stack as the close begins, not after the exit anim.
  // BUG27 layer 3 — routed through `dismiss`: while the confirm panel shows, the
  // hardware Back leaves the confirm step first, then closes the sheet.
  useOverlayRegistration(open, dismiss);

  useEffect(() => {
    if (open) {
      // Defer focus until the sheet has mounted — opens the soft keyboard with
      // the caret ready (T27.2.7.8 AC1).
      const id = window.setTimeout(() => inputRef.current?.focus(), 40);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  // Cancel a pending exit timer on unmount.
  useEffect(
    () => () => {
      if (exitTimer.current !== null) window.clearTimeout(exitTimer.current);
    },
    [],
  );

  // BUG27 layer 3 — on the confirm panel appearing, move focus INTO the new step
  // (not stranded on the now-gone "Add '{X}'" row): the heading when close
  // matches exist, else the "Add as new product" button. Deferred a tick so the
  // panel has rendered.
  useEffect(() => {
    if (confirmName === null) return undefined;
    const id = window.setTimeout(() => {
      const target = confirmHeadingRef.current ?? confirmAddNewRef.current;
      target?.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, [confirmName]);

  // Any surface can open the sheet via the shared `openCapture(prefill?)`
  // event (cold-start CTA, common-starter rows, Suggested "+ Add"). A prefill
  // seeds the text field so a starter chip lands ready to submit. This keeps a
  // single inline-add flow across the shell rather than a second add modal.
  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<CaptureOpenDetail>).detail;
      // Cancel any in-flight exit so a quick reopen lands open, not mid-close.
      if (exitTimer.current !== null) {
        window.clearTimeout(exitTimer.current);
        exitTimer.current = null;
      }
      setClosing(false);
      setText(detail?.prefill ?? '');
      setError(null);
      setDragY(0);
      setOpen(true);
    };
    window.addEventListener(CAPTURE_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(CAPTURE_OPEN_EVENT, onOpen);
  }, []);

  // Escape dismisses: the confirm step first (BUG27 layer 3), then the sheet.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, dismiss]);

  // Add a ShoppingListItem for a specific HouseholdProduct — the primary,
  // picker-driven path (tap a row). Reused by the create-on-the-fly path once a
  // typed name resolves (or is created) into a HouseholdProduct.
  const addProduct = async (product: HouseholdProductWithDetails) => {
    setError(null);

    const listId = listQuery.data?.list?.id;
    if (!listId) {
      setError('No active list. Create one from Settings.');
      return;
    }

    // The added quantity reflects the product's configured "How many to add"
    // (`default_quantity`, set via the stepper in HouseholdProductSettings). A
    // null / 0 / undefined value (a brand-new product, or one never configured)
    // floors at 1. Used for BOTH the optimistic row and the request below.
    const qty =
      product.default_quantity && product.default_quantity >= 1
        ? product.default_quantity
        : 1;

    setBusy(true);
    const nowIso = new Date().toISOString();

    try {
      // Mint the optimistic id INSIDE the try (BUG35): if id-minting ever throws
      // (an Android WebView without a usable crypto), the throw routes to the
      // catch/setError below instead of becoming a silent unhandled rejection.
      const optimistic: ShoppingListItemWithDetails = {
        id: `optimistic-${randomUuid()}`,
        shoppinglist_id: listId,
        householdproduct_id: product.id,
        selected_retaileroffer_id: null,
        quantity: qty,
        quantity_unit: null,
        checked: false,
        notes: null,
        status: 'pending',
        purchased_at: null,
        purchased_by: null,
        added_by: null,
        created_at: nowIso,
        updated_at: nowIso,
        list_name: '',
        household_id: product.household_id,
        product_name: product.display_name,
        display_title: product.display_name,
      };

      await orchestrators.ShoppingLists.handleAddItem({
        listId,
        optimistic,
        request: { householdproductId: product.id, quantity: qty },
      });
      closeSheet();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to add item'));
      setBusy(false);
    }
  };

  // T12.5 — tap-to-add a suggested item. A `ProductSuggestion` is lighter than
  // a `HouseholdProductWithDetails`, so it can't go straight into `addProduct`.
  // Bridge it: the add path only reads `id`, `display_name`, and `household_id`
  // off the product (for the request + the optimistic row). Borrow `household_id`
  // from any existing product (the same pattern `createAndAdd` uses) — the
  // backend scopes the add to the session's household, so the borrowed value is
  // cosmetic until the server reconciles. Routes through the existing add path;
  // no second add flow.
  const addSuggestion = async (suggestion: ProductSuggestion) => {
    const householdId =
      allProducts[0]?.household_id ?? activeProducts[0]?.household_id ?? '';
    // The endpoint computes `suggested_quantity` as the learned/typical amount
    // (falling back to the product's default_quantity), so it carries the
    // configured quantity for a suggested row. Flow it through `default_quantity`
    // on the bridged product — addProduct floors it at 1 (null/0/undefined → 1).
    const suggestedQty =
      suggestion.suggested_quantity && suggestion.suggested_quantity >= 1
        ? suggestion.suggested_quantity
        : 1;
    const bridged = {
      id: suggestion.householdproduct_id,
      household_id: householdId,
      display_name: suggestion.product_name,
      default_quantity: suggestedQty,
    } as unknown as HouseholdProductWithDetails;
    await addProduct(bridged);
  };

  // Create-on-the-fly (T27.2.7.8): create a new HouseholdProduct for the typed
  // name (its best-match SystemProduct link lands silently on the backend), then
  // add a ShoppingListItem for it. On a duplicate-name 409 the backend rejects
  // the create — fall back to adding for the existing product if we can find it.
  const createAndAdd = async (name: string) => {
    setError(null);
    setBusy(true);

    const nowIso = new Date().toISOString();
    // The optimistic row's household_id is cosmetic until the server reconciles
    // it (the backend scopes the create to the session's household). Borrow it
    // from any existing product when present.
    const householdId =
      allProducts[0]?.household_id ?? activeProducts[0]?.household_id ?? '';

    let created: HouseholdProductWithDetails;
    try {
      // Mint the optimistic id INSIDE the try (BUG35) — see addProduct above.
      const optimistic: HouseholdProductWithDetails = {
        id: `optimistic-${randomUuid()}`,
        household_id: householdId,
        display_name: name,
        category_id: null,
        notes: null,
        tags: [],
        image_url: null,
        default_quantity: 1,
        retailer_preferences: [],
        allow_substitutions: true,
        substitution_notes: null,
        always_keep_stocked: false,
        expected_price_value: null,
        expected_price_currency: 'CAD',
        expected_price_notes: null,
        seed_householdproduct_id: null,
        seeded_at: null,
        created_by: null,
        created_at: nowIso,
        updated_at: nowIso,
        category_name: null,
        linked_offer_count: 0,
        favorite_offer_count: 0,
        default_offer_id: null,
        default_offer_title: null,
        default_offer_price: null,
        default_store_name: null,
      };

      created = await orchestrators.HouseholdProducts.handleAddProduct({
        optimistic,
        request: { displayName: name },
      });
    } catch (err) {
      // Duplicate-name 409 (or a race that just landed the product): fall back
      // to adding a ShoppingListItem for the existing HouseholdProduct rather
      // than surfacing the create error. Search the active (search) result set.
      const existing = findProductByName(activeProducts, name);
      if (existing) {
        // addProduct manages its own busy/error state.
        await addProduct(existing);
        return;
      }
      setError(apiErrorMessage(err, 'Failed to add item'));
      setBusy(false);
      return;
    }

    // The HouseholdProduct create succeeded — `busy` is reset by addProduct's
    // own success/failure path (it sets busy true again first).
    await addProduct(created);
  };

  // Submit / "add what I typed": a name matching an existing HouseholdProduct
  // adds a ShoppingListItem for it (no duplicate create); any other non-empty
  // name opens the create-confirm step (BUG27 layer 3) rather than creating
  // instantly. A trimmed-empty name is a no-op. Any typed name always lands
  // somewhere — there is no dead-end.
  const addByName = async (rawName: string) => {
    const name = rawName.trim();
    if (!name) return;

    // Resolve against the active (search) result set — the backend `ilike`
    // search surfaces a name sorting past the capped initial page, so this
    // finds the existing product the capped list used to miss (BUG27). An EXACT
    // match skips the confirm and just adds.
    const product = findProductByName(activeProducts, name);
    if (product) {
      await addProduct(product);
      return;
    }
    // No exact match — hold for one beat: open the confirm panel. The fuller
    // explainer shows once per session, the condensed line after.
    setError(null);
    setConfirmFuller(!confirmExplainerSeen);
    confirmExplainerSeen = true;
    setConfirmName(name);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    void addByName(text);
  };

  // Scroll-to-dismiss-keyboard (T27.2.7.8 AC3): scrolling the suggestions region
  // past a small threshold blurs the input, which dismisses the soft keyboard so
  // the list gets the full sheet. The blur is the cross-platform mechanism (it
  // closes the WebView keyboard); native-only niceties stay gated behind
  // `Capacitor.isNativePlatform()`.
  const onListScroll = (e: ReactUIEvent<HTMLUListElement>) => {
    if (e.currentTarget.scrollTop <= SCROLL_DISMISS_PX) return;
    if (document.activeElement !== inputRef.current) return;
    // Blur dismisses the soft keyboard on both platforms (it closes the WebView
    // keyboard natively and the on-screen keyboard on web). No native-only
    // nicety is needed today; `dismissNativeKeyboard` is the gated seam for a
    // future `Keyboard.hide()` without touching the web path.
    inputRef.current?.blur();
    dismissNativeKeyboard();
  };

  // Tap-to-restore (T27.2.7.8 AC3): tapping/focusing the input brings the
  // keyboard back after a scroll dismissed it. Plain focus on both platforms.
  const onInputPointerDown = () => {
    inputRef.current?.focus();
  };

  // Swipe-to-dismiss (D2): the grabber row captures the pointer; a downward drag
  // tracks the finger and a release past SWIPE_DISMISS_PX closes the sheet (below
  // it the sheet snaps back to 0).
  const onGrabberPointerDown = (e: ReactPointerEvent) => {
    dragStartY.current = e.clientY;
    setDragY(0);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onGrabberPointerMove = (e: ReactPointerEvent) => {
    if (dragStartY.current === null) return;
    const delta = e.clientY - dragStartY.current;
    setDragY(delta > 0 ? delta : 0);
  };

  const onGrabberPointerUp = () => {
    if (dragStartY.current === null) return;
    const shouldClose = dragY >= SWIPE_DISMISS_PX;
    dragStartY.current = null;
    setDragY(0);
    // BUG27 layer 3 — a swipe-down while confirming leaves the confirm step
    // first (returns to the list), then a second swipe closes the sheet.
    if (shouldClose) dismiss();
  };

  const dragging = dragY > 0;
  const panelDynamicStyle: CSSProperties = dragging
    ? { ...panelStyle, transform: `translateY(${dragY}px)`, animation: 'none' }
    : panelStyle;

  return (
    <>
      <button
        type="button"
        style={fabStyle}
        onClick={() => setOpen(true)}
        aria-label="Quick add an item"
        aria-haspopup="dialog"
        data-testid="capture-fab"
      >
        <Icon name="plus" data-testid="capture-fab-icon" />
        <span style={micBadgeStyle} aria-hidden="true">
          <Icon name="mic" data-testid="capture-fab-mic-icon" />
        </span>
      </button>

      {onScreen && (
        <div
          className={`shopista-sheet-overlay${closing ? ' is-closing' : ''}`}
          style={overlayStyle}
          data-testid="capture-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) dismiss();
          }}
        >
          <div
            role="dialog"
            aria-label="Quick add"
            aria-modal="true"
            className={`shopista-sheet-panel${closing ? ' is-closing' : ''}`}
            style={panelDynamicStyle}
            data-testid="capture-sheet"
          >
            <div
              style={grabberRowStyle}
              data-testid="capture-grabber"
              aria-hidden="true"
              onPointerDown={onGrabberPointerDown}
              onPointerMove={onGrabberPointerMove}
              onPointerUp={onGrabberPointerUp}
              onPointerCancel={onGrabberPointerUp}
            >
              <span style={grabberStyle} />
            </div>

            <h3 style={titleStyle}>Add to your list</h3>

            <form onSubmit={handleSubmit} style={formStyle} data-testid="capture-form">
              <div className="field-focus-ring" style={fieldRowStyle}>
                <input
                  ref={inputRef}
                  type="text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onPointerDown={onInputPointerDown}
                  placeholder="What do you need?"
                  aria-label="Item name"
                  style={inputStyle}
                  data-testid="capture-input"
                />
                <button
                  type="submit"
                  style={addButtonStyle}
                  aria-label="Add item"
                  data-testid="capture-add"
                  disabled={busy}
                >
                  <Icon name="plus" data-testid="capture-add-icon" />
                </button>
              </div>
            </form>

            {confirmName !== null ? (
              // BUG27 layer 3 — the create-confirm panel REPLACES the
              // suggestions region in place. Close matches (when any) sit on top
              // as the easy reuse path; one explainer line; the single "Add as
              // new product" create commit; Back returns to the list.
              <div
                style={confirmPanelStyle}
                data-testid="capture-create-confirm"
              >
                {confirmMatches.length > 0 && (
                  <>
                    <p
                      ref={confirmHeadingRef}
                      style={confirmHeadingStyle}
                      tabIndex={-1}
                      data-testid="capture-confirm-heading"
                    >
                      {CONFIRM_HEADING}
                    </p>
                    <ul style={confirmMatchListStyle}>
                      {confirmMatches.map((product) => (
                        <li key={product.id}>
                          <button
                            type="button"
                            style={productRowStyle}
                            onClick={() => void addProduct(product)}
                            data-testid={`capture-confirm-match-${product.id}`}
                            disabled={busy}
                          >
                            <span>{product.display_name}</span>
                            <span style={productRowAddStyle} aria-hidden="true">
                              <Icon name="plus" />
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                <p
                  style={confirmExplainerStyle}
                  data-testid="capture-confirm-explainer"
                >
                  {confirmFuller
                    ? CONFIRM_EXPLAINER_FULL
                    : CONFIRM_EXPLAINER_CONDENSED}
                </p>

                <button
                  ref={confirmAddNewRef}
                  type="button"
                  style={confirmAddNewStyle}
                  onClick={() => void createAndAdd(confirmName)}
                  data-testid="capture-confirm-add-new"
                  disabled={busy}
                >
                  {CONFIRM_ADD_NEW_LABEL}
                </button>

                <button
                  type="button"
                  style={confirmBackStyle}
                  onClick={closeCreateConfirm}
                  data-testid="capture-confirm-back"
                  disabled={busy}
                >
                  {CONFIRM_BACK_LABEL}
                </button>
              </div>
            ) : allProducts.length === 0 && !trimmed ? (
              <p style={hintStyle} data-testid="capture-empty-catalog">
                You don&apos;t have any products yet. Type a name above and tap ＋
                to add your first one.
              </p>
            ) : (
              // T12.5 (prediction-led empty state) — the empty input (`!isSearching`)
              // is now Suggested-LED: when suggestions exist we show ONLY the
              // "Suggested · likely needed soon" group and DROP the full
              // alphabetical "your products" browse list. The browse list returns
              // only when (a) searching — it carries the search results + the
              // create-on-the-fly row — or (b) the empty state has no suggestions
              // (the fallback, so the sheet is never empty). The two cases are
              // mutually exclusive with the Suggested-only state by `showBrowse`.
              <>
                {showSuggestions && (
                  <>
                    <p
                      style={sectionLabelStyle}
                      data-testid="capture-suggestions-label"
                    >
                      Suggested · likely needed soon
                    </p>
                    <ul
                      style={suggestionListStyle}
                      data-testid="capture-suggestions-list"
                    >
                      {suggestions.map((suggestion) => (
                        <li key={suggestion.householdproduct_id}>
                          <button
                            type="button"
                            style={productRowStyle}
                            onClick={() => void addSuggestion(suggestion)}
                            data-testid={`capture-suggestion-${suggestion.householdproduct_id}`}
                            disabled={busy}
                          >
                            <span style={suggestionTextColStyle}>
                              <span>{suggestion.product_name}</span>
                              {suggestion.reason_text && (
                                <span style={suggestionReasonStyle}>
                                  {suggestion.reason_text}
                                </span>
                              )}
                            </span>
                            <span style={productRowAddStyle} aria-hidden="true">
                              <Icon name="plus" />
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {showBrowse && (
                  <>
                    <p style={sectionLabelStyle} data-testid="capture-section-label">
                      your products
                    </p>
                    <ul
                      style={listStyle}
                      data-testid="capture-product-list"
                      onScroll={onListScroll}
                    >
                      {showAddTyped && (
                        <li key="__add-typed">
                          <button
                            type="button"
                            style={addTypedRowStyle}
                            onClick={() => void addByName(text)}
                            data-testid="capture-add-typed"
                            disabled={busy}
                          >
                            <span>Add “{trimmed}”</span>
                            <span style={productRowAddStyle} aria-hidden="true">
                              <Icon name="plus" />
                            </span>
                          </button>
                        </li>
                      )}
                      {filteredProducts.map((product) => (
                        <li key={product.id}>
                          <button
                            type="button"
                            style={productRowStyle}
                            onClick={() => void addProduct(product)}
                            data-testid={`capture-product-${product.id}`}
                            disabled={busy}
                          >
                            <span>{product.display_name}</span>
                            <span style={productRowAddStyle} aria-hidden="true">
                              <Icon name="plus" />
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </>
            )}

            {error && (
              <p style={errorStyle} role="alert" data-testid="capture-error">
                {error}
              </p>
            )}
            {/* Voice slot demoted out of the input's primary row (Flow A) — the
                stub stays reachable as a footer hint; no transcription built. */}
            <p style={voiceFooterStyle} data-testid="capture-voice-hint">
              <Icon name="mic" data-testid="capture-hint-mic-icon" />
              Voice capture is coming soon.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
